import { initializeApp } from "firebase/app";
import {
  getFirestore,
  getDocs,
  collection,
  addDoc,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  getDownloadURL,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import { getMessaging } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const fire = initializeApp(firebaseConfig);
export const db = getFirestore(fire);
export const cloudStorage = getStorage(fire);
export const auth = getAuth(fire);
export const messaging = getMessaging(fire);

// Request browser notification permission and get FCM token (for admin web push)
export const requestNotificationPermission = async () => {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;
    const { getToken } = await import("firebase/messaging");
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });
    return token;
  } catch (err) {
    console.warn("FCM token error:", err);
    return null;
  }
};

export const getData = async (col) => {
  const snapshot = await getDocs(collection(db, col));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addItem = async (col, item) => {
  const docRef = await addDoc(collection(db, col), {
    ...item,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
};

export const setItem = async (col, item) => {
  const docRef = doc(collection(db, col));
  await setDoc(docRef, { ...item, uid: docRef.id, createdAt: serverTimestamp() });
  return docRef.id;
};

export const updateItem = async (col, id, item) => {
  await setDoc(doc(db, col, id), { ...item, updatedAt: serverTimestamp() }, { merge: true });
};

export const deleteItem = async (col, id) => {
  await deleteDoc(doc(db, col, id));
};

export const uploadImage = async (file, address) => {
  const storageRef = ref(cloudStorage, address);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

export const updateImage = async (file, oldAddress, newAddress) => {
  try {
    await deleteObject(ref(cloudStorage, oldAddress));
  } catch (_) {}
  return await uploadImage(file, newAddress);
};

export const subscribeToCollection = (col, callback, onError) => {
  return onSnapshot(
    collection(db, col),
    (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError
  );
};
